import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AirplaneTilt, PencilSimple, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
    { value: 'economy', label: 'Economy Class', lessons: 25 },
    { value: 'business', label: 'Business Class', lessons: 35 },
    { value: 'first_class', label: 'First Class', lessons: 45 },
    { value: 'spoedopleiding_5', label: 'Spoedopleiding 5 uur', lessons: 5, group: 'spoedopleiding' },
    { value: 'spoedopleiding_10', label: 'Spoedopleiding 10 uur', lessons: 10, group: 'spoedopleiding' },
    { value: 'spoedopleiding_16_5', label: 'Spoedopleiding 16,5 uur', lessons: 16.5, group: 'spoedopleiding' },
    { value: 'spoedopleiding_25', label: 'Spoedopleiding 25 uur', lessons: 25, group: 'spoedopleiding' },
];

export default function EditStudentModal({ student, onClose, onSuccess, studentMode = false }) {
    const [name, setName] = useState(student.name || '');
    const [email, setEmail] = useState(student.email || '');
    const [phone, setPhone] = useState(student.phone || '');
    const [notes, setNotes] = useState(student.notes || '');
    const [packageType, setPackageType] = useState(student.package_type || '');
    const [showSpoedOpties, setShowSpoedOpties] = useState((student.package_type || '').startsWith('spoedopleiding'));
    const [examDate, setExamDate] = useState(student.exam_date || '');
    const [examTime, setExamTime] = useState(student.exam_time || '');
    const [examTableNumber, setExamTableNumber] = useState(student.exam_table_number || '');
    const [paymentTotal, setPaymentTotal] = useState(student.payment_total || '');
    const [paymentInstallments, setPaymentInstallments] = useState(student.payment_installments || 0);
    const [paidInstallments, setPaidInstallments] = useState(student.paid_installments || []);
    const [initialLessons, setInitialLessons] = useState(student.initial_lessons ?? '');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const emailChanged = (email || '').trim().toLowerCase() !== (student.email || '').toLowerCase();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            if (studentMode) {
                const payload = {
                    name: name.trim(),
                    email: email.trim(),
                    phone: phone.trim(),
                };
                Object.keys(payload).forEach(k => payload[k] === '' && delete payload[k]);
                await axios.put(`${API}/students/me/profile`, payload, { withCredentials: true });
            } else {
                const trimmedPaid = (paidInstallments || []).filter(p => p < Number(paymentInstallments));
                // Parse initial_lessons: accept Dutch comma "19,5" or dot "19.5"
                const initialStr = String(initialLessons ?? '').replace(',', '.').trim();
                const initialNum = initialStr === '' ? 0 : parseFloat(initialStr);
                const trimmedTable = examTableNumber.trim();
                const payload = {
                    name: name.trim(),
                    email: email.trim(),
                    phone: phone.trim(),
                    notes,
                    package_type: packageType || null,
                    exam_date: examDate || null,
                    exam_time: examTime || null,
                    payment_total: paymentTotal === '' ? 0 : Number(paymentTotal),
                    payment_installments: Number(paymentInstallments) || 0,
                    paid_installments: trimmedPaid,
                    initial_lessons: Number.isFinite(initialNum) ? initialNum : 0,
                };
                Object.keys(payload).forEach(k => (payload[k] === null || payload[k] === '') && delete payload[k]);
                // exam_table_number: allow empty string through so instructor can CLEAR the field.
                payload.exam_table_number = trimmedTable;
                await axios.put(`${API}/students/${student.id}`, payload, { withCredentials: true });
            }
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.detail || 'Er is iets misgegaan');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-lg bg-[#2D3748] border-[#4A5568] text-white max-h-[90vh] overflow-y-auto" data-testid="edit-student-modal">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <PencilSimple size={22} className="text-[#D4A537]" weight="fill" />
                        Gegevens wijzigen
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                        {studentMode
                            ? 'Werk je eigen gegevens bij. Pakket en examendatum worden door je rijinstructeur beheerd.'
                            : `Werk de gegevens van ${student.name} bij. Het e-mailadres wordt gesynchroniseerd met het login-account.`}
                    </DialogDescription>
                </DialogHeader>

                {error && (
                    <div className="p-3 bg-red-900/50 border border-red-700 rounded-md text-red-300 text-sm" data-testid="edit-student-error">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-gray-300">Naam *</Label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                className="bg-[#1A202C] border-[#4A5568] text-white"
                                data-testid="edit-student-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-300">E-mail *</Label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="bg-[#1A202C] border-[#4A5568] text-white"
                                data-testid="edit-student-email"
                            />
                        </div>
                    </div>

                    {emailChanged && (
                        <div className="flex items-start gap-2 p-3 rounded-md border border-[#D4A537]/40 bg-[#D4A537]/10 text-[#D4A537] text-sm" data-testid="email-change-warning">
                            <Warning size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
                            <div>
                                Je wijzigt het e-mailadres. De leerling moet vanaf nu inloggen met <strong className="text-white">{email || '...'}</strong>. Het wachtwoord blijft hetzelfde.
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-gray-300">Telefoonnummer</Label>
                            <Input
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="06-12345678"
                                className="bg-[#1A202C] border-[#4A5568] text-white"
                                data-testid="edit-student-phone"
                            />
                        </div>
                        {!studentMode && (
                            <div className="space-y-2">
                                <Label className="text-gray-300">Examendatum</Label>
                                <div className="flex gap-2">
                                    <Input
                                        type="date"
                                        value={examDate}
                                        onChange={(e) => setExamDate(e.target.value)}
                                        className="bg-[#1A202C] border-[#4A5568] text-white"
                                        data-testid="edit-student-examdate"
                                    />
                                    <Input
                                        type="time"
                                        value={examTime}
                                        onChange={(e) => setExamTime(e.target.value)}
                                        className="bg-[#1A202C] border-[#4A5568] text-white w-28"
                                        data-testid="edit-student-examtime"
                                    />
                                </div>
                                <div className="pt-1">
                                    <Label className="text-gray-400 text-xs uppercase tracking-wider">Tafelnummer <span className="text-gray-500 normal-case tracking-normal">(optioneel — later invullen kan ook)</span></Label>
                                    <Input
                                        type="text"
                                        value={examTableNumber}
                                        onChange={(e) => setExamTableNumber(e.target.value)}
                                        placeholder="bijv. 12 of A3"
                                        maxLength={10}
                                        className="bg-[#1A202C] border-[#4A5568] text-white mt-1"
                                        data-testid="edit-student-exam-table-number"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {!studentMode && (
                    <div className="space-y-3">
                        <Label className="text-gray-300">Lespakket</Label>
                        <div className="grid grid-cols-2 gap-3">
                            {PACKAGES.filter(p => !p.group).map(pkg => (
                                <button
                                    key={pkg.value}
                                    type="button"
                                    onClick={() => { setPackageType(pkg.value); setShowSpoedOpties(false); }}
                                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                                        packageType === pkg.value
                                            ? 'border-[#D4A537] bg-[#D4A537]/10'
                                            : 'border-[#4A5568] bg-[#1A202C] hover:border-[#D4A537]/50'
                                    }`}
                                    data-testid={`edit-package-${pkg.value}`}
                                >
                                    <div className="flex items-center justify-between mb-0.5">
                                        <span className={`font-semibold text-sm ${packageType === pkg.value ? 'text-[#D4A537]' : 'text-white'}`}>
                                            {pkg.label}
                                        </span>
                                        <AirplaneTilt size={16} weight="fill" className={packageType === pkg.value ? 'text-[#D4A537]' : 'text-gray-500'} />
                                    </div>
                                    <div className="text-xs text-gray-400">{pkg.lessons} lessen</div>
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => setShowSpoedOpties(!showSpoedOpties)}
                                className={`col-span-2 p-3 rounded-lg border-2 text-left transition-all ${
                                    (packageType || '').startsWith('spoedopleiding')
                                        ? 'border-[#D4A537] bg-[#D4A537]/10'
                                        : 'border-[#4A5568] bg-[#1A202C] hover:border-[#D4A537]/50'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-0.5">
                                    <span className={`font-semibold text-sm ${(packageType || '').startsWith('spoedopleiding') ? 'text-[#D4A537]' : 'text-white'}`}>
                                        Spoedopleiding Nederland
                                    </span>
                                    <AirplaneTilt size={16} weight="fill" className={(packageType || '').startsWith('spoedopleiding') ? 'text-[#D4A537]' : 'text-gray-500'} />
                                </div>
                                <div className="text-xs text-gray-400">
                                    {(packageType || '').startsWith('spoedopleiding')
                                        ? PACKAGES.find(p => p.value === packageType)?.label
                                        : 'Klik om uren te kiezen'}
                                </div>
                            </button>
                        </div>
                        {showSpoedOpties && (
                            <div className="grid grid-cols-2 gap-3 ml-2 pl-4 border-l-2 border-[#D4A537]/30">
                                {PACKAGES.filter(p => p.group === 'spoedopleiding').map(pkg => (
                                    <button
                                        key={pkg.value}
                                        type="button"
                                        onClick={() => setPackageType(pkg.value)}
                                        className={`p-2.5 rounded-lg border-2 text-left transition-all ${
                                            packageType === pkg.value
                                                ? 'border-[#D4A537] bg-[#D4A537]/10'
                                                : 'border-[#4A5568] bg-[#1A202C] hover:border-[#D4A537]/50'
                                        }`}
                                        data-testid={`edit-package-${pkg.value}`}
                                    >
                                        <span className={`font-semibold text-sm ${packageType === pkg.value ? 'text-[#D4A537]' : 'text-white'}`}>
                                            {pkg.label}
                                        </span>
                                        <div className="text-xs text-gray-400">{pkg.lessons} lessen</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    )}

                    {!studentMode && (
                    <div className="space-y-2 p-3 bg-[#1A202C] rounded-lg border border-[#4A5568]" data-testid="edit-initial-lessons-section">
                        <Label className="text-gray-300 flex items-center gap-1.5">
                            🚗 Al gereden lessen
                            <span className="text-xs text-gray-500 font-normal">(overstapper)</span>
                        </Label>
                        <p className="text-xs text-gray-500 -mt-1">
                            Aantal lessen dat de leerling al heeft gehad voordat hij/zij bij jou kwam. Telt direct mee in de "X/Y lessen gebruikt"-teller. Gebruik halve waarden (bv. 19,5) voor 30/90-minuten lessen.
                        </p>
                        <Input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]+([.,][0-9])?"
                            value={initialLessons}
                            onChange={(e) => setInitialLessons(e.target.value)}
                            placeholder="0"
                            className="bg-[#2D3748] border-[#4A5568] text-white w-32"
                            data-testid="edit-student-initial-lessons"
                        />
                    </div>
                    )}

                    {!studentMode && (
                    <div className="space-y-3 p-3 bg-[#1A202C] rounded-lg border border-[#4A5568]" data-testid="edit-payment-section">
                        <Label className="text-gray-300 flex items-center gap-1.5">💰 Termijn betaling</Label>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs text-gray-400 uppercase tracking-wider">Totaalbedrag (€)</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={paymentTotal}
                                    onChange={(e) => setPaymentTotal(e.target.value)}
                                    placeholder="bijv. 1800"
                                    className="bg-[#2D3748] border-[#4A5568] text-white"
                                    data-testid="edit-payment-total"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs text-gray-400 uppercase tracking-wider">Aantal termijnen</Label>
                                <div className="grid grid-cols-6 gap-1">
                                    {[0, 1, 2, 3, 4, 5].map(n => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => {
                                                setPaymentInstallments(n);
                                                if (n === 0) setPaidInstallments([]);
                                                else setPaidInstallments(prev => prev.filter(p => p < n));
                                            }}
                                            className={`h-9 rounded-md border-2 font-bold text-sm transition-all ${
                                                Number(paymentInstallments) === n
                                                    ? 'border-[#D4A537] bg-[#D4A537]/15 text-[#D4A537]'
                                                    : 'border-[#4A5568] bg-[#2D3748] text-gray-400 hover:border-[#D4A537]/50'
                                            }`}
                                            data-testid={`edit-installments-${n}`}
                                        >
                                            {n === 0 ? '—' : `${n}×`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {Number(paymentInstallments) > 0 && Number(paymentTotal) > 0 && (
                            <div className="text-xs text-gray-400">
                                {paymentInstallments} × <span className="text-[#D4A537] font-semibold">€{(Number(paymentTotal) / Number(paymentInstallments)).toFixed(2)}</span> = €{Number(paymentTotal).toFixed(2)}
                            </div>
                        )}
                        {Number(paymentInstallments) > 0 && (
                            <div className="space-y-1.5">
                                <Label className="text-xs text-gray-400 uppercase tracking-wider">Reeds betaald (vink aan)</Label>
                                <div className="flex flex-wrap gap-2">
                                    {Array.from({ length: Number(paymentInstallments) }, (_, i) => {
                                        const isPaid = paidInstallments.includes(i);
                                        return (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => {
                                                    setPaidInstallments(prev =>
                                                        prev.includes(i) ? prev.filter(p => p !== i) : [...prev, i].sort((a, b) => a - b)
                                                    );
                                                }}
                                                className={`px-3 py-2 rounded-md border-2 text-sm font-semibold transition-all flex items-center gap-1.5 ${
                                                    isPaid
                                                        ? 'border-emerald-500 bg-emerald-500/15 text-emerald-400'
                                                        : 'border-[#4A5568] bg-[#2D3748] text-gray-400 hover:border-emerald-500/50'
                                                }`}
                                                data-testid={`edit-paid-${i}`}
                                            >
                                                {isPaid ? '✓' : '○'} Termijn {i + 1}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {!studentMode && (
                    <div className="space-y-2">
                        <Label className="text-gray-300">Notities</Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            className="bg-[#1A202C] border-[#4A5568] text-white"
                            data-testid="edit-student-notes"
                        />
                    </div>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                            data-testid="edit-student-cancel"
                        >
                            Annuleren
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold"
                            data-testid="edit-student-submit"
                        >
                            {loading ? 'Opslaan...' : 'Opslaan'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
